<script setup lang="ts">
import {
  COMMON_RESET_PAGE_ON,
  commonFilterFields,
  enumField,
  PaginationComponent,
  SegmentListItem,
  type SegmentsFilter,
  useQueryFilter,
} from '@metapic-apps/shared'
import { computed } from 'vue'
import { useRouter } from 'vue-router'

import { useGetSegments } from '@/api'
import PlusIcon from '@/assets/icons/add-plus.svg?component'
import LoadingIcon from '@/assets/icons/loading.svg?component'
import { FilterComponent } from '@/components/Filter'
import HeaderComponent from '@/components/Navigation/HeaderComponent.vue'
import type { FilterType } from '@/lib/types/common.types'
import { useUserStore } from '@/stores/user'
import { BoxComponent, ButtonComponent, FlexComponent, PageWrapperComponent, TypographyComponent } from '@/ui-library'

const userStore = useUserStore()
const router = useRouter()

const { filter, reset } = useQueryFilter<SegmentsFilter>(
  {
    page: commonFilterFields.page,
    query: commonFilterFields.query,
    sortBy: commonFilterFields.sortBy,
    advertiserGroup: commonFilterFields.advertiserGroup,
    type: enumField('type', ['static', 'dynamic']),
    createdByMe: commonFilterFields.createdByMe,
  },
  {
    page: 1,
    sortBy: 'updated_at:desc',
    advertiserGroup: {
      id: userStore.user.storeGroupId,
      name: '',
    },
  },
  { resetPageOn: COMMON_RESET_PAGE_ON, cleared: { page: 1, sortBy: 'updated_at:desc' } },
)

const { data, isLoading, totalPages } = useGetSegments(filter)

const filterModel = computed({
  get: () => filter.value as unknown as FilterType,
  set: (value) => {
    filter.value = value as unknown as SegmentsFilter
  },
})

const typeOptions = [
  { label: 'Dynamic', value: 'dynamic' },
  { label: 'Static', value: 'static' },
]

const sortingItems = [
  { label: 'Last updated', value: 'updated_at' },
  { label: 'Created', value: 'created_at' },
  { label: 'Most creators', value: 'member_count' },
]

const isEmpty = computed(() => !isLoading.value && data.value.length === 0)

const handleCreate = () => router.push({ name: 'segmentCreate' })
const handleEdit = (id: string) => router.push({ name: 'segmentEdit', params: { id } })
</script>
<template>
  <HeaderComponent>
    <ButtonComponent variant="secondary" @click="handleCreate">
      <template #iconLeft>
        <ElIcon :size="18">
          <PlusIcon />
        </ElIcon>
      </template>
      Create segment
    </ButtonComponent>
  </HeaderComponent>
  <main id="segments-page" class="segments-view__wrapper">
    <PageWrapperComponent>
      <BoxComponent :mb="32">
        <FilterComponent
          v-model="filterModel"
          :sorting-items="sortingItems"
          :status-options="typeOptions"
          :more-filters-items="[]"
          status-field-key="type"
          has-separate-order-sorting
          show-status-selector
          show-more-filters-selector
          show-sorting-selector
          placeholder="Search segments"
          @clear="reset"
        />
      </BoxComponent>
      <FlexComponent v-if="isEmpty" justify="center">
        <TypographyComponent variant="body-large"> There are no segments that match your filters </TypographyComponent>
      </FlexComponent>
      <div v-if="isLoading" class="segments-view__loading">
        <ElIcon :size="96">
          <LoadingIcon />
        </ElIcon>
      </div>
      <FlexComponent v-if="!isLoading && data.length > 0" :gap="16" direction="column">
        <button
          v-for="segment in data"
          :key="segment.id"
          type="button"
          class="segments-view__item"
          :aria-label="`Edit segment ${segment.name}`"
          @click="handleEdit(segment.id)"
        >
          <SegmentListItem :segment="segment" />
        </button>
      </FlexComponent>
    </PageWrapperComponent>
    <BoxComponent v-if="totalPages !== 0 && data.length > 0" :pt="32" :pb="32" class="segments-view__pagination">
      <PaginationComponent v-model:current-page="filter.page" :total-pages="totalPages" />
    </BoxComponent>
  </main>
</template>
<style lang="scss" scoped>
.segments-view__item {
  display: block;
  width: 100%;
  padding: 0;
  border: none;
  background: none;
  text-align: inherit;
  cursor: pointer;
}
.segments-view__wrapper {
  position: relative;
}
.segments-view__loading {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
}
.segments-view__pagination {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
}
</style>
